import React from 'react';
import { featureMetrics } from '../../data/dashboardMetrics';
import { EnhancedMetricCard } from './EnhancedMetricCard';
import { ActivityTimeline } from './ActivityTimeline';
import { FeaturedMetric } from './FeaturedMetric';
interface InteractiveDashboardPreviewProps {
  activeFeature: number;
}
const dashboardTitles: Record<number, string> = {
  0: 'Deal Volume & Market Overview',
  1: 'Performance & Conversion Analytics',
  2: 'Pipeline & Deal Management',
  3: 'Market Intelligence & Trends'
};
const dashboardDescriptions: Record<number, string> = {
  0: 'Track your offers, deals, and market performance in real-time',
  1: 'Monitor conversion rates and optimize your offer strategies',
  2: 'Manage your pipeline and track deals through every stage',
  3: 'Analyze market trends and identify high-performing areas'
};
/**
 * InteractiveDashboardPreview component
 * Premium dashboard preview with enhanced metrics, timeline, and animations
 */
export function InteractiveDashboardPreview({
  activeFeature
}: InteractiveDashboardPreviewProps) {
  const metrics = featureMetrics[activeFeature];
  return <div className="sticky top-8 w-full bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] rounded-2xl border border-[#3ab795]/20 shadow-[0_8px_48px_rgba(0,0,0,0.4),0_0_0_1px_rgba(58,183,149,0.1)] overflow-hidden transition-all duration-500" data-id="element-504">
      {/* Dashboard Header */}
      <div className="bg-gradient-to-r from-[#0a0a0a]/90 to-[#0a0a0a]/80 border-b border-white/5 px-6 py-4 flex items-center justify-between backdrop-blur-sm" data-id="element-505">
        <div className="flex items-center gap-3" data-id="element-506">
          <div className="flex gap-1.5" data-id="element-507">
            <div className="w-3 h-3 rounded-full bg-red-500/80 hover:bg-red-500 transition-colors cursor-pointer" data-id="element-508" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/80 hover:bg-yellow-500 transition-colors cursor-pointer" data-id="element-509" />
            <div className="w-3 h-3 rounded-full bg-green-500/80 hover:bg-green-500 transition-colors cursor-pointer" data-id="element-510" />
          </div>
          <span className="text-sm font-medium text-white/60" data-id="element-511">Dashboard</span>
        </div>
        <div className="flex items-center gap-2" data-id="element-512">
          <div className="w-2 h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-513" />
          <span className="text-xs text-[#3ab795] font-medium" data-id="element-514">Live</span>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="p-6 space-y-6 max-h-[calc(600px-60px)] overflow-y-auto custom-scrollbar" data-id="element-515">
        {/* Title */}
        <div className="space-y-2" data-id="element-516">
          <h3 className="text-2xl font-bold text-white transition-all duration-500" data-id="element-517">
            {dashboardTitles[activeFeature]}
          </h3>
          <p className="text-sm text-[#b8bcc8]" data-id="element-518">
            {dashboardDescriptions[activeFeature]}
          </p>
        </div>

        {/* Featured Metric */}
        <FeaturedMetric activeFeature={activeFeature} data-id="element-519" />

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-4" data-id="element-520">
          {metrics.map((metric, index) => <EnhancedMetricCard key={`${activeFeature}-${index}`} title={metric.title} value={metric.value} trend={metric.trend} icon={metric.icon} delay={index * 100} data-id="element-521" />)}
        </div>

        {/* Activity Timeline */}
        <div className="bg-[#0a0a0a]/60 border border-white/5 rounded-xl p-5 hover:border-[#3ab795]/20 transition-all duration-300" data-id="element-522">
          <ActivityTimeline activeFeature={activeFeature} data-id="element-523" />
        </div>
      </div>

      {/* Bottom Accent */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#3ab795] to-transparent" data-id="element-524" />

      {/* Corner Glow */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-[#3ab795]/10 rounded-full blur-3xl pointer-events-none" data-id="element-525" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#3ab795]/10 rounded-full blur-3xl pointer-events-none" data-id="element-526" />
    </div>;
}